import { Alert, Box, Button, FormControl, TextField } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardPageLabel, ClipboardPageLabelRef } from "routes/lens-editor/page-labels/ClipboardPageLabel";
import EditableClipboardPageLabel from "routes/lens-editor/page-labels/EditableClipboardPageLabel";
import { useFetcher, useLocation } from "react-router-dom";
import { useFetcherWithSnackbar } from "routes/lens-editor/page-labels/useFetcherWithSnackbar";
import { useNavigate } from "react-router";
import { usePageLabelsLoader } from "loaders/page-labels.loader";

const PageLabelsImport = () => {
  const [contents, setContents] = useState("");
  const clipboardPageLabels: ClipboardPageLabel[] = useMemo(() => (contents ? JSON.parse(contents) : []), [contents]);
  const pageLabelRefs = useRef<(ClipboardPageLabelRef | null)[]>([]);
  const fetcher = useFetcher();
  const fetcherResponse = useFetcherWithSnackbar(fetcher, "Page Labels imported", "Unable to import Page Labels");
  const location = useLocation();
  const navigate = useNavigate();
  const pageLabels = usePageLabelsLoader();
  const pageLabelNameCollision = useMemo(
    () => clipboardPageLabels.find((cpl) => pageLabels.find((pl) => pl.label.name === cpl.label.name)),
    [pageLabels, clipboardPageLabels]
  );

  const onImport = () => {
    fetcher.submit(JSON.stringify(pageLabelRefs.current), {
      encType: "application/json",
      method: "POST",
      action: location.pathname,
    });
  };

  useEffect(() => {
    if (fetcherResponse.completed) {
      navigate("../list");
    }
  }, [fetcherResponse.completed]);

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      {!contents && (
        <FormControl fullWidth>
          <TextField value={contents} onChange={(evt) => setContents(evt.target.value)} label="Paste Page Labels" />
        </FormControl>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", height: "100%" }}>
        {pageLabelNameCollision !== undefined ? (
          <Alert severity="error">One or more Page Labels with the same name exist</Alert>
        ) : (
          clipboardPageLabels.map((pl, index) => (
            <EditableClipboardPageLabel
              ref={(element) => (pageLabelRefs.current[index] = element)}
              key={index}
              label={pl.label}
              rules={pl.rules}
            />
          ))
        )}
      </Box>

      {pageLabelNameCollision === undefined && clipboardPageLabels.length > 0 && (
        <div>
          <Button sx={{ mt: 2 }} variant="contained" onClick={onImport}>
            Import
          </Button>
        </div>
      )}
    </Box>
  );
};

export default PageLabelsImport;
